import { FormState, SlotKey } from './formTypes';
import { SLOT_KEYS } from './constants';
import { cleanColumnDefinitions } from './formRules';
import { parseJsonArray } from './jsonRules';

export type UploadedFile = { slot_key: string; blob_path: string; file_name: string; file_size?: number };

export type GenerateBody = {
    run_id: string;
    run_mode: 'new' | 'resume';
    requested_by_email?: string;
    catalog_mode?: string;
    product_type?: string;
    extract_observations?: boolean;
    extract_solutions?: boolean;
    extract_diagnostics?: boolean;
    cluster_by_part_id?: boolean;
    use_custom_instructions?: boolean;
    use_solution_code_non_semantic_clustering?: boolean;
    rule_based_obs_label_merge?: boolean;
    use_exact_match_error_code_clustering?: boolean;
    use_exact_match_solution_code_clustering?: boolean;
    part_id_as_solution_name?: 'off' | 'duplicate' | 'replace';
    max_cases?: number;
    files?: UploadedFile[];
    previous_run?: string;
    start_stage?: string;
    column_definitions?: { columns: string[]; definition: string; relevant_to: string[] }[];
    solution_code_non_semantic_clustering_rules?: unknown[];
    custom_post_processing_rules?: unknown[];
};

export const buildGenerateBody = (runId: string, form: FormState, uploaded: UploadedFile[], requestedByEmail?: string): GenerateBody => {
    if (form.runMode === 'resume') {
        return {
            run_id: runId,
            run_mode: 'resume',
            requested_by_email: requestedByEmail,
            previous_run: form.previousRun ?? undefined,
            start_stage: form.startStage ?? undefined,
        };
    }
    const columnDefinitions = cleanColumnDefinitions(form);
    return {
        run_id: runId,
        run_mode: 'new',
        requested_by_email: requestedByEmail,
        catalog_mode: form.catalogMode ?? undefined,
        ...(form.productType.trim() !== '' ? { product_type: form.productType.trim() } : {}),
        extract_observations: form.extractObservations,
        extract_solutions: form.extractSolutions,
        extract_diagnostics: form.extractDiagnostics,
        // Deliberately not user-facing: part-id clustering is always on. The
        // Advanced toggle was removed rather than defaulted, so there is no
        // form field to read.
        cluster_by_part_id: true,
        use_custom_instructions: form.useCustomInstructions,
        use_solution_code_non_semantic_clustering: form.useSolutionCodeNonSemanticClustering,
        rule_based_obs_label_merge: form.ruleBasedObsLabelMerge,
        use_exact_match_error_code_clustering: form.useExactMatchErrorCodeClustering,
        use_exact_match_solution_code_clustering: form.useExactMatchSolutionCodeClustering,
        solution_code_non_semantic_clustering_rules:
            form.useSolutionCodeNonSemanticClustering
                ? parseJsonArray(form.solutionCodeNonSemanticClusteringRules)
                : [],
        custom_post_processing_rules:
            form.useCustomPostProcessing
                ? parseJsonArray(form.customPostProcessingRules)
                : [],
        max_cases: form.maxCasesEnabled ? form.maxCases : undefined,
        files: uploaded,
        // Always sent, 'off' included: omitting the key let the worker's DEFAULTS
        // fill in 'duplicate', which silently turned the feature on.
        part_id_as_solution_name: form.partIdAsSolutionName,
        ...(columnDefinitions.length > 0 ? { column_definitions: columnDefinitions } : {}),
    };
};

// Minimal interface for the API methods this orchestrator needs (satisfied by MainApi).
export interface CatalogApi {
    getCatalogUploadUrls: (tenantId: string, files: { slot_key: string; filename: string }[]) =>
        Promise<{ run_id: string; uploads: { slot_key: string; filename: string; blob_path: string; upload_url: string }[] }>;
    submitCatalogGenerate: (tenantId: string, body: GenerateBody) =>
        Promise<{ success: boolean; run_id?: string; description?: string }>;
}

export const uploadToBlob = async (uploadUrl: string, file: File): Promise<void> => {
    const response = await fetch(uploadUrl, { method: 'PUT', headers: { 'x-ms-blob-type': 'BlockBlob' }, body: file });
    if (!response.ok) {
        throw new Error(`Upload of ${file.name} failed (HTTP ${response.status}).`);
    }
};

export const runCatalogGeneration = async (
    api: CatalogApi,
    form: FormState,
    requestedByEmail?: string,
    onUploadProgress?: (uploaded: number, total: number) => void,
) => {
    const tenantId = form.tenantId!;
    if (form.runMode === 'resume') {
        return api.submitCatalogGenerate(tenantId, buildGenerateBody('', form, [], requestedByEmail));
    }
    const present = SLOT_KEYS
        .map((k: SlotKey) => ({ k, file: form.files[k] }))
        .filter((x) => x.file !== null) as { k: SlotKey; file: File }[];
    const urlResp = await api.getCatalogUploadUrls(
        tenantId, present.map(({ k, file }) => ({ slot_key: k, filename: file.name })),
    );
    if (!urlResp || !Array.isArray(urlResp.uploads)) {
        throw new Error('Upload URL request returned no upload targets.');
    }
    const byKey = new Map(urlResp.uploads.map((u) => [u.slot_key, u]));
    // Validate every slot BEFORE uploading anything. Checking inside the loop below
    // would PUT the earlier files first and only then throw, leaving those blobs
    // orphaned — nothing references them, because submitCatalogGenerate never runs.
    // (A mid-upload network failure can still orphan blobs; that needs a storage
    // lifecycle rule, not a client-side guard.)
    const missingTargets = present.map(({ k }) => k).filter((k) => !byKey.has(k));
    if (missingTargets.length > 0) {
        throw new Error(
            `Upload URL request did not include upload targets for: ${missingTargets.join(', ')}.`,
        );
    }
    const uploaded: UploadedFile[] = [];
    onUploadProgress?.(0, present.length);
    for (const { k, file } of present) {
        const u = byKey.get(k)!;
        await uploadToBlob(u.upload_url, file);
        uploaded.push({ slot_key: k, blob_path: u.blob_path, file_name: file.name, file_size: file.size });
        onUploadProgress?.(uploaded.length, present.length);
    }
    return api.submitCatalogGenerate(tenantId, buildGenerateBody(urlResp.run_id, form, uploaded, requestedByEmail));
};
